import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Rocket, Landmark, Filter, Gauge } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABELS = {
    new: 'Nouvelles', contacted: 'Contactées', qualified: 'Qualifiées',
    proposal: 'Proposition', won: 'Gagnées', lost: 'Perdues',
};
const STAGE_ORDER = ['new', 'contacted', 'qualified', 'proposal', 'won', 'lost'];
const eur = (n) => `${Number(n || 0).toLocaleString('fr-FR')} €`;

export function FunnelPanel({ auth }) {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    useEffect(() => {
        if (!auth) return;
        axios.get(`${API}/super-admin/analytics/funnel?days=${days}`, auth).then(r => setData(r.data)).catch(() => { });
    }, [auth, days]);
    if (!data) return null;

    const max = Math.max(1, ...(data.steps || []).map(s => s.count));
    return (
        <div className="card-soft p-6" data-testid="sa-funnel-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
                <div className="flex items-center gap-2">
                    <Filter size={17} className="text-primary" />
                    <h3 className="font-heading text-lg">Tunnel de conversion</h3>
                </div>
                <div className="flex items-center gap-1.5">
                    {[7, 30, 90].map(d => (
                        <button key={d} onClick={() => setDays(d)}
                            className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${days === d ? 'bg-foreground text-background border-foreground' : 'border-border text-foreground/65 hover:border-foreground/40'}`}
                            data-testid={`funnel-days-${d}`}>
                            {d} j
                        </button>
                    ))}
                </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
                Visiteur → inscription → premier projet ({data.total_events} événements sur {data.days} jours — mesure first-party, sans cookie).
            </p>
            {data.total_events === 0 ? (
                <p className="text-sm text-foreground/50 py-4 text-center">Aucun événement encore collecté — les visites du site public alimenteront ce tunnel.</p>
            ) : (
                <div className="space-y-2.5">
                    {data.steps.map((s, i) => (
                        <div key={s.key} className="flex items-center gap-3" data-testid={`funnel-step-${s.key}`}>
                            <span className="text-xs w-44 shrink-0 text-foreground/75">{s.label}</span>
                            <div className="flex-1 h-2.5 rounded-full bg-surface-muted overflow-hidden">
                                <div className="h-full bg-secondary rounded-full transition-all duration-500"
                                    style={{ width: `${Math.round((s.count / max) * 100)}%` }} />
                            </div>
                            <span className="text-xs font-mono w-24 text-right text-muted-foreground">
                                {s.count}{i > 0 && s.pct_prev !== null ? ` · ${s.pct_prev}%` : ''}
                            </span>
                        </div>
                    ))}
                </div>
            )}
            {data.extras?.some(e => e.count > 0) && (
                <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
                    {data.extras.map(e => (
                        <span key={e.key} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-muted text-xs text-foreground/75"
                            data-testid={`funnel-extra-${e.key}`}>
                            {e.label} : <strong className="font-mono">{e.count}</strong>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

const LEVEL_BADGE = {
    fragile: 'bg-red-50 text-red-700 border-red-200',
    structuration: 'bg-amber-50 text-amber-800 border-amber-200',
    prometteur: 'bg-blue-50 text-blue-700 border-blue-200',
    solide: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export function ScoresPanel({ auth }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!auth) return;
        axios.get(`${API}/super-admin/project-scores?limit=10`, auth).then(r => setData(r.data)).catch(() => { });
    }, [auth]);
    if (!data || !data.items?.length) return null;
    return (
        <div className="card-soft p-6" data-testid="sa-scores-panel">
            <div className="flex items-center gap-2 mb-1">
                <Gauge size={17} className="text-primary" />
                <h3 className="font-heading text-lg">Derniers Project Scores™</h3>
                <span className="text-xs text-muted-foreground ml-1">({data.total} au total)</span>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
                Les visiteurs avec email + score élevé sont vos prospects les plus chauds — retrouvez-les dans le CRM (source « project-score »).
            </p>
            <div className="space-y-2">
                {data.items.map(s => (
                    <div key={s.id} className="flex items-center gap-3 flex-wrap text-sm border-b border-border/40 pb-2"
                        data-testid={`sa-score-row-${s.id}`}>
                        <span className="font-mono font-semibold w-14">{s.total}/100</span>
                        <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${LEVEL_BADGE[s.level?.key] || ''}`}>
                            {s.level?.label}
                        </span>
                        <span className="text-foreground/75 truncate flex-1 min-w-[120px]">
                            {s.project_name || <span className="text-foreground/40">Projet non nommé</span>}
                        </span>
                        {s.email
                            ? <span className="text-xs text-secondary font-medium" data-testid="sa-score-email">{s.email}</span>
                            : <span className="text-xs text-foreground/40">anonyme</span>}
                        <span className="text-[11px] text-muted-foreground">{new Date(s.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function ActivationStatsPanel({ auth }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!auth) return;
        axios.get(`${API}/super-admin/activation-stats`, auth).then(r => setData(r.data)).catch(() => { });
    }, [auth]);
    if (!data) return null;

    return (
        <div className="card-soft p-6" data-testid="sa-activation-stats">
            <div className="flex items-center gap-2 mb-1">
                <Rocket size={17} className="text-primary" />
                <h3 className="font-heading text-lg">Activation des inscrits gratuits</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
                {data.total_free} compte{data.total_free > 1 ? 's' : ''} Explorer · {data.avg_steps}/5 étapes en moyenne ·{' '}
                <strong className="text-foreground">{data.activated_percent}% activés à 100 %</strong> ·{' '}
                conversion vers payant : <strong className="text-foreground">{data.conversion_percent}%</strong> ({data.paid_plans} plans payants)
            </p>
            {data.total_free === 0 ? (
                <p className="text-sm text-foreground/50 py-4 text-center">Aucun inscrit gratuit pour le moment.</p>
            ) : (
                <div className="space-y-2.5">
                    {data.steps.map(s => (
                        <div key={s.key} className="flex items-center gap-3" data-testid={`activation-stat-${s.key}`}>
                            <span className="text-xs w-32 shrink-0 text-foreground/75">{s.label}</span>
                            <div className="flex-1 h-2.5 rounded-full bg-surface-muted overflow-hidden">
                                <div className="h-full bg-primary rounded-full transition-all duration-500"
                                    style={{ width: `${s.percent}%` }} />
                            </div>
                            <span className="text-xs font-mono w-20 text-right text-muted-foreground">
                                {s.count} · {s.percent}%
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export function ConsortiumPipelinePanel({ auth }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!auth) return;
        axios.get(`${API}/super-admin/consortium-pipeline`, auth).then(r => setData(r.data)).catch(() => { });
    }, [auth]);
    if (!data) return null;

    return (
        <div className="card-soft p-6 border-indigo-200" data-testid="sa-consortium-pipeline">
            <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                    <Landmark size={17} className="text-indigo-600" />
                    <h3 className="font-heading text-lg">🏛️ Pipeline Institution · Consortium (B2G)</h3>
                </div>
                <div className="text-right">
                    <div className="font-heading text-2xl text-indigo-700" data-testid="consortium-total-potential">{eur(data.total_potential_eur)}</div>
                    <div className="text-[11px] text-muted-foreground">revenu annuel prévisionnel</div>
                </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
                {data.total_leads} lead{data.total_leads > 1 ? 's' : ''} · 2 500 €/an par consortium (5 000 € au-delà de 10 organisations) ·
                gagné : <strong className="text-emerald-700">{eur(data.won_value_eur)}</strong>
            </p>
            {data.total_leads === 0 ? (
                <p className="text-sm text-foreground/50 py-4 text-center">
                    Aucun lead Consortium pour le moment — partagez la page /p/consortium avec vos contacts bailleurs.
                </p>
            ) : (
                <>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
                        {STAGE_ORDER.map(st => {
                            const d = data.by_stage[st];
                            return (
                                <div key={st} className={`rounded-lg border p-2.5 text-center ${d ? 'border-indigo-200 bg-indigo-50/50' : 'border-border opacity-50'}`}>
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{STAGE_LABELS[st]}</div>
                                    <div className="font-heading text-lg">{d?.count || 0}</div>
                                    <div className="text-[10px] font-mono text-indigo-700">{d ? eur(d.potential_eur) : '—'}</div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="space-y-1.5">
                        {data.items.slice(0, 5).map(l => (
                            <div key={l.id} className="flex items-center justify-between gap-3 text-xs rounded-lg border border-border px-3 py-2">
                                <span className="truncate">
                                    <strong>{l.organization || l.full_name}</strong>
                                    <span className="text-muted-foreground"> · {l.team_size || '?'} · {STAGE_LABELS[l.stage] || l.stage}</span>
                                </span>
                                <span className="font-mono text-indigo-700 shrink-0">{eur(l.potential_eur)}/an</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
